import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { onboardingApi, Profile, profileApi } from '../api';
import { useAuth } from '../contexts/auth/auth-context';
import { trackEvent } from '../utils/analytics';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  has_audio: boolean;
  has_files: boolean;
  created_at: string;
  updated_at: string;
}

interface OnboardingQuestion {
  id: string;
  conversation_id: string;
  content: string;
  has_audio: boolean;
  has_files: boolean;
  created_at: string;
  updated_at: string;
  answer?: Message | undefined;
  audio_url?: string;
  audio_id?: string;
  is_answered: boolean;
  title?: string;
  step_order?: number;
}

type OnboardingContextValue = {
  userProfile: Profile | null;

  questions: OnboardingQuestion[];
  currentQuestionIndex: number;

  onboardingBootLoading: boolean;
  onboardingReady: boolean;

  isLoading: boolean;
  isCompleted: boolean;
  hasAnsweredAnyQuestion: boolean;
  hasQuestions: boolean;
  totalQuestions: number;

  submitAnswer: (
    conversationId: string,
    content: string,
    files?: File[],
    audio?: File,
    audioDuration?: number,
    onUploadProgress?: (progress: number) => void,
    transcribePending?: boolean,
  ) => Promise<void>;

  nextQuestion: () => void;
  previousQuestion: () => void;

  refreshQuestions: () => Promise<void>;
  loadQuestions: (silent?: boolean) => Promise<void>;

  markOnboardingCompleted: () => Promise<void>;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, userId, profileReadyForRouting, sessionLoading, session, user } = useAuth();

  const authed = useMemo(() => {
    if (sessionLoading) return false;
    if (user) return true;
    if (session?.access_token) return true;
    return false;
  }, [sessionLoading, user, session]);

  const [questions, setQuestions] = useState<OnboardingQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isCompletedState, setIsCompletedState] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const [onboardingBootLoading, setOnboardingBootLoading] = useState(true);
  const onboardingReady = !onboardingBootLoading;

  const lastProfileKeyRef = useRef<string | null>(null);

  const hasQuestions = questions.length > 0;
  const isCompleted = isCompletedState || profile?.onboarding_completed === true;

  const hasAnsweredAnyQuestion =
    totalQuestions > 0 && (isCompleted || questions.some((q) => q.is_answered));

  const loadQuestions = useCallback(
    async (silent = false) => {
      // ✅ Only load if authed + routing-ready
      if (authed && !profileReadyForRouting) return;
      if (authed && !profile) return;

      const token = localStorage.getItem('access_token');
      if (!token) {
        // not authed -> "ready but empty"
        setQuestions([]);
        setIsCompletedState(false);
        setTotalQuestions(0);
        return;
      }

      if (!silent) setIsLoading(true);

      try {
        const response = await onboardingApi.getStatus();

        if (response.questions) {
          setIsCompletedState(response.status === 'completed');
          setTotalQuestions(response.total_questions);

          const mappedQuestions: OnboardingQuestion[] = response.questions
            .map((q: any, index: number): OnboardingQuestion | null => {
              const questionMsg = q.messages?.[0];
              if (!questionMsg) return null;

              const answerMsg =
                q.messages.length > 1 ? q.messages[q.messages.length - 1] : undefined;

              return {
                id: questionMsg.id,
                conversation_id: q.conversation_id,
                content: questionMsg.content,
                has_audio: !!(questionMsg.audios && questionMsg.audios.length > 0),
                has_files: !!(questionMsg.files && questionMsg.files.length > 0),
                created_at: questionMsg.created_at,
                updated_at: questionMsg.created_at,
                audio_url:
                  questionMsg.audios && questionMsg.audios.length > 0
                    ? questionMsg.audios[0].audio_url
                    : undefined,
                audio_id:
                  questionMsg.audios && questionMsg.audios.length > 0
                    ? questionMsg.audios[0].id
                    : undefined,
                is_answered: q.messages.length > 1,
                title: q.title,
                step_order: index + 1,
                answer: answerMsg
                  ? {
                      id: answerMsg.id,
                      conversation_id: answerMsg.conversation_id,
                      sender_id: answerMsg.sender_id,
                      content: answerMsg.content,
                      has_audio: !!(answerMsg.audios && answerMsg.audios.length > 0),
                      has_files: !!(answerMsg.files && answerMsg.files.length > 0),
                      created_at: answerMsg.created_at,
                      updated_at: answerMsg.created_at,
                    }
                  : undefined,
              };
            })
            .filter((q: OnboardingQuestion | null): q is OnboardingQuestion => q !== null);

          setQuestions(mappedQuestions);

          if (!silent) {
            const firstUnansweredIndex = mappedQuestions.findIndex((q) => !q.is_answered);
            if (firstUnansweredIndex !== -1) setCurrentQuestionIndex(firstUnansweredIndex);
            else
              setCurrentQuestionIndex(mappedQuestions.length > 0 ? mappedQuestions.length - 1 : 0);
          }
        } else {
          setQuestions([]);
          setIsCompletedState(false);
          setTotalQuestions(0);
        }
      } catch (error) {
        console.error('[OnboardingContext] Error loading onboarding questions:', error);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [authed, profileReadyForRouting, profile]
  );

  const refreshQuestions = useCallback(async () => {
    await loadQuestions(true);
  }, [loadQuestions]);

  // ✅ onboarding boot: wait until auth is routing-ready (if authed), then load once
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (sessionLoading) return;

      if (authed) {
        if (!profileReadyForRouting) return;
        if (!profile) return;
      }

      const profileId = profile?.id || 'guest';
      const regCompleted = profile?.registration_completed || false;
      const onboardingCompleted = profile?.onboarding_completed || false;
      const currentKey = `${profileId}:${regCompleted}:${onboardingCompleted}`;

      if (!onboardingBootLoading && lastProfileKeyRef.current === currentKey) {
        return;
      }
      lastProfileKeyRef.current = currentKey;

      try {
        await loadQuestions(false);
      } finally {
        if (!cancelled) setOnboardingBootLoading(false);
      }
    };

    boot();

    return () => {
      cancelled = true;
    };
  }, [authed, sessionLoading, profileReadyForRouting, profile, loadQuestions, onboardingBootLoading]);

  const submitAnswer = useCallback(
    async (
      conversationId: string,
      content: string,
      files?: File[],
      audio?: File,
      audioDuration?: number,
      onUploadProgress?: (progress: number) => void,
      transcribePending?: boolean,
    ) => {
      try {
        const response = await onboardingApi.submitAnswer(
          conversationId,
          content,
          files,
          audio,
          audioDuration,
          onUploadProgress,
          transcribePending,
        );

        const updatedQuestions = questions.map((q) => {
          if (q.conversation_id === conversationId) {
            return {
              ...q,
              is_answered: true,
              answer: {
                id: 'temp-id',
                conversation_id: conversationId,
                sender_id: userId || 'unknown',
                content,
                has_audio: !!audio,
                has_files: !!files?.length,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            };
          }
          return q;
        });

        setQuestions(updatedQuestions);

        trackEvent('onboarding_answer_submitted', {
          question_index: currentQuestionIndex,
          has_audio: !!audio,
          has_files: !!(files && files.length > 0),
        });

        if (response?.onboarding_completed === true || response?.status === 'completed') {
          setIsCompletedState(true);
        }
      } catch (error) {
        console.error('Error submitting answer:', error);
        throw error;
      }
    },
    [questions, userId, currentQuestionIndex]
  );

  const nextQuestion = useCallback(
    () => setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1)),
    [questions.length]
  );

  const previousQuestion = useCallback(
    () => setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0)),
    []
  );

  const markOnboardingCompleted = useCallback(async () => {
    try {
      await profileApi.updateMe({
        onboarding_completed: true,
        registration_completed: true,
      });
      setIsCompletedState(true);
      trackEvent('onboarding_completed');
      // Signal to the prompt queue that the user just finished onboarding
      // so the Chat page can fire the 'onboarding_to_chat' trigger.
      try { sessionStorage.setItem('mailander_just_finished_onboarding', 'true'); } catch { /* ignore */ }
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
      throw error;
    }
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        userProfile: profile ?? null,

        questions,
        currentQuestionIndex,

        onboardingBootLoading,
        onboardingReady,

        isLoading,
        isCompleted,
        hasAnsweredAnyQuestion,
        hasQuestions,
        totalQuestions,

        submitAnswer,
        nextQuestion,
        previousQuestion,

        refreshQuestions,
        loadQuestions,

        markOnboardingCompleted,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
